<template>
  <div class="details">
    <div class="details__title px-5"></div>

    <div class="details__content container">
      <h1 class="title is-3">{{ $t("trains.title") }}</h1>
      <div class="columns is-multiline details__content__box m-0 mb-5">
        <div
          class="field column is-one-third-desktop"
          v-if="
            companyInfo.userSettings?.canBookOnBehalfOfOthers &&
            companyInfo.allowListedEmployees &&
            businessUnits
          "
        >
          <h5 class="title is-5">
            {{ $t("TrainReservationForm.findEmployee") }}
          </h5>
          <EmployeeSelection
            :is-trains="true"
            :pre-selected-employees="guests"
            :business-units="businessUnits"
            @selected-employee="addEmployee"
          />
          <p v-if="companyInfo.allowListedEmployees" class="mt-3">
            {{ $t("TrainReservationForm.unknownEmployee") }}
          </p>
        </div>
        <div
          class="field column is-one-third-desktop"
          v-if="
            !companyInfo?.userSettings?.canBookOnBehalfOfOthers ||
            (!companyInfo.allowUnlistedEmployees &&
              !companyInfo.allowListedEmployees)
          "
        >
          <h5 class="title is-5">
            {{ $t("TrainReservationForm.personalReservationHeader") }}
          </h5>
          <p>{{ $t("TrainReservationForm.personalReservation") }}</p>
        </div>

        <div
          class="field column is-one-third-desktop"
          v-if="
            companyInfo.userSettings?.canBookOnBehalfOfOthers &&
            companyInfo.allowUnlistedEmployees
          "
        >
          <AddUnlistedEmployee
            :is-trains="true"
            @created-unlisted-employee="addUnlistedEmployee"
          />
        </div>

        <div class="field column is-one-third-desktop">
          <template v-if="companyInfo.employeeIsRequired">
            <h5 class="title is-5">
              {{ $t("TrainReservationForm.selectedPassengers") }}
            </h5>
            <br />
            <i
              v-if="
                selectedEmployees.length == 0 &&
                selectedUnlistedEmployees.length == 0
              "
            >
              {{ $t("TrainReservationForm.noPassengersAdded") }}
            </i>
            <div
              class="is-flex is-align-items-center is-justify-content-space-between mb-2"
              v-for="e in selectedEmployees"
              :key="e"
            >
              <span class="u-weight--bold"
                >{{ e.firstName }} {{ e.lastName }}</span
              >
              <button
                class="c-button c-button--small is-danger"
                @click.stop.prevent="removeSelectedEmployee(e)"
              >
                <span class="icon is-small">
                  <i class="fas fa-times"></i>
                </span>
              </button>
            </div>
            <br />
            <br />
          </template>
          <div
            class="is-flex is-align-items-center is-justify-content-space-between mb-2"
            v-for="e in selectedUnlistedEmployees"
            :key="e"
          >
            <strong
              >{{ e.firstName }} {{ e.lastName }} ({{ e.emailAddress }})</strong
            >
            <button
              class="c-button c-button--small is-danger"
              @click.stop.prevent="removeUnlistedEmployees(e)"
            >
              <span class="icon is-small">
                <i class="fas fa-times"></i>
              </span>
            </button>
          </div>
          <SelectedBusinessUnit
            :business-units="businessUnits"
            :selected-business-unit="selectedBusinessUnit"
            @selected-business-unit="selectBusinessUnit"
            @remove-business-unit="removeBusinessUnit"
          />
        </div>
      </div>
      <div class="columns is-full-width details__content__box m-0 mb-5">
        <div class="column">
          <h5 class="title is-5">
            {{ $t("TrainReservationForm.tripType") }}
          </h5>
          <div class="field">
            <label class="radio">
              <input
                type="radio"
                name="tripType"
                value="oneWay"
                v-model="tripType"
              />
              {{ $t("TrainReservationForm.oneWay") }}
            </label>
            <label class="radio">
              <input
                type="radio"
                name="tripType"
                value="roundTrip"
                v-model="tripType"
              />
              {{ $t("TrainReservationForm.return") }}
            </label>
          </div>
        </div>

        <div class="column">
          <div class="field">
            <label class="label">{{ $t("TrainLegs.originStation") }}</label>
            <div class="control">
              <input
                type="text"
                class="input"
                v-model="searchTermOrigin"
                :placeholder="$t('TrainLegs.originPlaceholder')"
                autocomplete="off"
                @focus="
                  originTrainStationSelected = true;
                  destinationTrainStationSelected = false;
                "
              />
              <div v-if="originTrainStationSelected && trainStations.length">
                <div
                  v-for="station in trainStations"
                  :key="station.name"
                  class="dropdown-item"
                  @click.stop="selectOriginStation(station)"
                >
                  <span>
                    {{ station.name }} - {{ station.city }},
                    {{ station.country }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="field">
            <label class="label">{{
              $t("TrainLegs.destinationStation")
            }}</label>
            <div class="control">
              <input
                type="text"
                class="input"
                v-model="searchTermDestination"
                :placeholder="$t('TrainLegs.destinationPlaceholder')"
                autocomplete="off"
                @focus="
                  destinationTrainStationSelected = true;
                  originTrainStationSelected = false;
                "
              />
              <div
                v-if="
                  destinationTrainStationSelected && destinationStations.length
                "
              >
                <div
                  v-for="station in destinationStations"
                  :key="station.name"
                  class="dropdown-item"
                  @click.stop="selectDestinationStation(station)"
                >
                  <span>
                    {{ station.name }} - {{ station.city }},
                    {{ station.country }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="column">
          <div class="field">
            <label class="label">{{
              $t("TrainReservationForm.departureDate")
            }}</label>
            <Datepicker
              v-model="departureDateTrain"
              :enableTimePicker="false"
              autoApply
              :format="formatDate"
              :minDate="new Date()"
              locale="nl"
            />
          </div>
          <label class="label" v-if="tripType === 'roundTrip'">{{
            $t("TrainReservationForm.returnDate")
          }}</label>
          <div class="control" v-if="tripType === 'roundTrip'">
            <Datepicker
              v-model="returnDateTrain"
              :enableTimePicker="false"
              autoApply
              :format="formatDate"
              :minDate="departureDateTrain ?? new Date()"
              locale="nl"
            />
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label" for="arrivalTime">
              {{ $t("TrainReservationForm.arrivalTime") }}
            </label>
            <div class="control">
              <input
                type="time"
                class="input"
                v-model="outwardArrivalTime"
                id="arrivalTime"
              />
            </div>
          </div>
          <div class="field" v-if="tripType === 'roundTrip'">
            <label class="label" for="returnTime">
              {{ $t("TrainReservationForm.returnTime") }}
            </label>
            <div class="control">
              <input
                type="time"
                class="input"
                v-model="returnDepartureTime"
                id="returnTime"
              />
            </div>
          </div>
        </div>
      </div>

      <form
        @submit.prevent="makeReservation()"
        class="details__content__box columns is-multiline details__content__box m-0 mb-5"
      >
        <div class="field column is-half-desktop">
          <label for="notes">{{ $t("TrainReservationForm.notes") }}</label>
          <div class="control">
            <textarea
              class="textarea"
              v-model="notes"
              id="notes"
              name="notes"
              rows="5"
              :placeholder="$t('TrainReservationForm.trainNotesPlaceholder')"
            ></textarea>
          </div>
          <CustomField
            v-for="field in companyInfo.customFields"
            :key="field.name"
            :customField="field"
            @setted-value="setValue"
          ></CustomField>
        </div>
        <div class="field column is-half-desktop">
          <h6 class="title is-6">
            {{ $t("TrainReservationForm.trainClass") }}
          </h6>
          <div class="field">
            <label>
              <input type="radio" value="3" v-model="selectedTrainClass" />
              {{ $t("TrainReservationForm.firstClass") }}
            </label>
          </div>
          <div class="field">
            <label>
              <input type="radio" value="4" v-model="selectedTrainClass" />
              {{ $t("TrainReservationForm.secondClass") }}
            </label>
          </div>
          <div class="field">
            <label class="checkbox">
              <input type="checkbox" v-model="carRentalSelected" />
              {{ $t("CarReservationForm.CarRental") }}
            </label>
          </div>
          <CarReservationModule
            v-if="carRentalSelected"
            :available-drivers="availableDrivers"
            :carReturnDateUnknown="tripType != 'roundTrip'"
            :minCarReturnDate="departureDateTrain ?? new Date()"
            @car-data-changed="handleCarDataChanged"
          >
          </CarReservationModule>
          <button
            class="c-button c-button--primary mt-4"
            :disabled="disableButton"
            type="submit"
          >
            {{ $t("TrainReservationForm.sendRequest") }}
          </button>
        </div>
      </form>
    </div>

    <NotificationMessage
      :level="notification.level"
      :autohide="false"
      :title="notification.title"
      :message="notification.message"
      :show="notification.show"
      @toggle="toggleNotification"
    />
  </div>
</template>

<script>
import CommonHelper from "@/helpers/common.helper.js";
import SelectedBusinessUnit from "./components/reservationform/SelectedBusinessUnit.vue";
import EmployeeSelection from "./components/reservationform/EmployeeSelection.vue";
import AddUnlistedEmployee from "./components/reservationform/AddUnlistedEmployee.vue";
import CustomField from "./components/reservationform/CustomField.vue";
import CarReservationModule from "./components/reservationform/CarReservationModule.vue";
import moment from "moment";

export default {
  components: {
    SelectedBusinessUnit,
    EmployeeSelection,
    AddUnlistedEmployee,
    CustomField,
    CarReservationModule
  },
  data() {
    return {
      selectedTrainClass: "economy",
      selectedHoldLuggage: false,
      changesAllowed: true,
      isLoading: false,
      legs: [],
      tripType: "roundTrip",
      isPricesLoading: false,
      disableButton: false,
      notes: "",
      guests: "",
      showEditableFields: false,
      returnedData: null,
      businessUnits: null,
      allowBusinessUnitClearing: false,
      selectedEmployees: [],
      selectedUnlistedEmployees: [],
      selectedBusinessUnit: null,
      selectedCarTransmissionType: null,
      errorMessages: [],
      companyInfo: {},
      reservationMeta: {
        businessUnit: null,
        location: null,
        dateRange: {
          startDate: null,
          endDate: null,
          startTime: null,
          endTime: null
        }
      },
      carRentalSelected: false,
      carData: {
        selectedCarClass: "",
        selectedCarTransmissionType: "",
        driver: {}
      },
      Driver: {},
      departureDateTrain: null,
      outwardArrivalTime: "",
      returnDateTrain: null,
      returnDepartureTime: "",
      customFieldFormData: {},
      isSaving: false,
      notification: {
        show: false,
        title: "",
        message: "",
        level: "info"
      },
      searchTermOrigin: "",
      searchTermDestination: "",
      trainStations: [],
      destinationStations: [],
      originStation: "",
      destinationStation: "",
      debounceTime: 600, // 600 milliseconds is nice as this keeps the time from the last keystroke to ui update to about 800 milliseconds
      originTrainStationSelected: false,
      destinationTrainStationSelected: false
    };
  },
  async created() {
    this.getCompanyInfo();

    let companyInfo = await CommonHelper.getCompanyInfo();
    if (companyInfo && companyInfo.budget) {
      this.budget = companyInfo.budget;
    }
  },
  computed: {
    numberOfPersons() {
      const selectedNumber =
        this.selectedEmployees.length + this.selectedUnlistedEmployees.length;
      return selectedNumber;
    },
    availableDrivers() {
      return [...this.selectedEmployees, ...this.selectedUnlistedEmployees];
    }
  },
  watch: {
    searchTermOrigin: {
      handler: function (newVal) {
        clearTimeout(this._originSearchTimeout);
        if (!newVal) {
          this.trainStations = [];
          return;
        }

        this._originSearchTimeout = setTimeout(async () => {
          try {
            this.trainStations = await this.searchStation(newVal);
          } catch (e) {
            console.error("Error fetching origin stations:", e);
            this.trainStations = [];
          }
        }, this.debounceTime);
      },
      immediate: false
    },
    searchTermDestination: {
      handler: function (newVal) {
        clearTimeout(this._destinationSearchTimeout);
        if (!newVal) {
          this.destinationStations = [];
          return;
        }

        this._destinationSearchTimeout = setTimeout(async () => {
          try {
            this.destinationStations = await this.searchStation(newVal);
          } catch (e) {
            console.error("Error fetching destination stations:", e);
            this.destinationStations = [];
          }
        }, this.debounceTime);
      },
      immediate: false
    }
  },
  methods: {
    loadDetails() {},
    setValue(data) {
      this.customFieldFormData[data.field] = data.value;
    },
    async getCompanyInfo() {
      this.companyInfo = await CommonHelper.getCompanyInfo();

      this.companyInfo.employeeIsRequired =
        this.companyInfo.userSettings.canBookOnBehalfOfOthers &&
        (this.companyInfo.allowUnlistedEmployees ||
          this.companyInfo.allowListedEmployees);

      this.companyInfo.customFields.sort((a, b) =>
        Number(a.mandatory) > Number(b.mandatory) ? -1 : 1
      );

      this.businessUnits = await CommonHelper.getBusinessUnits();
    },
    selectBusinessUnit(data) {
      this.selectedBusinessUnit = data.businessUnit;
    },
    removeBusinessUnit() {
      this.selectedBusinessUnit = null;
    },
    addEmployee(data) {
      const employeeAlreadySelected = this.selectedEmployees.find(
        (sE) => sE.id == data.id
      );

      if (!employeeAlreadySelected) {
        this.selectedEmployees.push(data.employee);

        if (this.selectedBusinessUnit == null && data.businessUnit) {
          this.selectBusinessUnit(data);
        }
      }
    },
    addEmployeeDriver(data) {
      // This method is kept for compatibility but the CarReservationModule handles driver selection internally
      if (data?.employee) {
        this.Driver = {
          id: data.employee.id,
          firstName: data.employee.firstName,
          lastName: data.employee.lastName,
          emailAddress: data.employee.emailAddress
        };
      } else {
        this.driverEmployee = null;
      }
    },
    handleCarDataChanged(data) {
      this.carData = data;
      // Update the legacy Driver object for compatibility
      this.Driver = data.driver || {};
    },
    removeSelectedDriver() {
      this.Driver = {};
      this.carData.driver = {};
    },
    removeSelectedEmployee(e) {
      this.selectedEmployees = this.selectedEmployees.filter(
        (se) => se.id != e.id
      );
    },
    addUnlistedEmployee(data) {
      this.selectedUnlistedEmployees.push(data.employee);
    },
    removeUnlistedEmployees(e) {
      this.selectedUnlistedEmployees = this.selectedUnlistedEmployees.filter(
        (uE) => uE.id != e.id
      );
    },
    async makeReservation() {
      let url = `${this.AVAILABILITY_API_URL}/reservation/train`;
      let dataIsValid = this.validateForm();

      if (dataIsValid) {
        this.isSaving = true;
        let notes = this.notes;
        const employees = this.selectedEmployees.map((e) => e.id);
        const unlistedEmployees = this.selectedUnlistedEmployees.map((e) => {
          return {
            firstName: e.firstName,
            lastName: e.lastName,
            emailAddress: e.emailAddress
          };
        });
        const businessUnitId = this.selectedBusinessUnit
          ? this.selectedBusinessUnit.id
          : null;

        let reservationList = [
          {
            customFields: this.customFieldFormData,
            travelClass: this.selectedTrainClass,
            businessUnitId: businessUnitId,
            DepartureLocation: this.searchTermOrigin,
            DestinationLocation: this.searchTermDestination,
            notes: notes,
            employeeIds: employees,
            guests: unlistedEmployees,
            travellers: unlistedEmployees,
            vehicleType: 1,
            departureDate: moment(this.departureDateTrain).format("YYYY-MM-DD"),
            outboundArrivalTime: this.outwardArrivalTime
          }
        ];

        if (this.tripType === "roundTrip") {
          reservationList[0].returnDate =
            moment(this.returnDateTrain)?.format("YYYY-MM-DD") ?? null;
          reservationList[0].returnDepartureTime =
            this.returnDepartureTime ?? null;
        } else {
          reservationList[0].returnDate = null;
          reservationList[0].returnDepartureTime = null;
        }

        if (this.carRentalSelected) {
          const carReservation = {
            carClass: this.carData.selectedCarClass,
            transmissionType: this.carData.selectedCarTransmissionType,
            departureLocation: this.searchTermOrigin, // Pickup from departure station
            destinationLocation: this.searchTermDestination, // Return to destination station
            departureDate: moment(this.departureDateTrain).format("YYYY-MM-DD"),
            departureTime: this.outwardArrivalTime,
            returnDate: null, // Will be set below based on trip type
            returnTime: null,
            customFields: this.customFieldFormData,
            notes: notes,
            businessUnitId: businessUnitId,
            vehicleType: "RENTAL_CAR",
            driver: this.carData.driver
          };

          // Add return date only for round trip
          if (this.tripType === "roundTrip" && this.returnDateTrain) {
            carReservation.returnDate = moment(this.returnDateTrain).format(
              "YYYY-MM-DD"
            );
            carReservation.returnTime = this.returnDepartureTime;
          } else if (
            this.tripType !== "roundTrip" &&
            this.carData.carReturnDate
          ) {
            carReservation.returnDate = moment(
              this.carData.carReturnDate
            ).format("YYYY-MM-DD");
            carReservation.returnTime = this.carData.carReturnTime;
          }

          reservationList.push(carReservation);
        }

        let saveData = {
          reservationList: reservationList
        };

        Object.keys(this.customFieldFormData).forEach((key) => {
          saveData.reservationList[0].customFields[key] =
            this.customFieldFormData[key];
        });

        this.saveData = JSON.stringify(saveData, null, " ");
        this.disableButton = true;

        const { status, data } = await this.axios.post(url, saveData);

        if (status == 200) {
          this.notification.title = this.$t("TrainReservationForm.savedTitle");
          this.notification.message = this.$t("success");
          this.notification.show = true;
          this.notification.level = "success";

          setTimeout(() => {
            this.notification.show = false;
            this.disableButton = false;
          }, 30 * 1000);

          let unlistedEmployeesCount = this.selectedUnlistedEmployees.length;
          let listedEmployeesCount = this.selectedEmployees.length;
          let employeeCount = unlistedEmployeesCount + listedEmployeesCount;

          // Switch to "trainReservation" for analytics, if that's purely internal
          // and does not conflict with an external system.
          let trackData = {
            reservationType: "train",
            departureStations: this.legs
              .map((l) => l.departureAirport.city)
              .join(", "),
            arrivalStations: this.legs
              .map((l) => l.arrivalAirport.city)
              .join(", "),
            legs: this.legs.length,
            numberOfPersons: employeeCount,
            status: "success"
          };
          this.track("trainReservation", trackData);

          this.$router.push("/train-confirmation");
        } else {
          this.notification.title = this.$t("TrainReservationForm.errorTitle");
          this.notification.message = this.$t(
            "TrainReservationForm.errorMessage"
          );
          this.notification.show = true;
          this.notification.level = "danger";
          this.disableButton = false;

          setTimeout(() => {
            this.notification.show = false;
          }, 30 * 1000);
        }
        this.isSaving = false;
      }
    },
    validateForm() {
      this.errorMessages = [];
      this.showNotification = false;

      const businessUnitMandatory = this.companyInfo.businessUnitMandatory;
      const businessUnitIsSelected = !!this.selectedBusinessUnit;
      const employeeIsRequired = this.companyInfo.employeeIsRequired;
      const employeeIsSelected =
        this.selectedUnlistedEmployees.length > 0 ||
        this.selectedEmployees.length > 0;

      let isValid = true;

      if (businessUnitMandatory && !businessUnitIsSelected) {
        isValid = false;
        this.errorMessages.push(this.$t("error.businessunitMandatory"));
      }

      if (employeeIsRequired && !employeeIsSelected) {
        isValid = false;
        this.errorMessages.push(this.$t("error.employeeMandatory"));
      }

      if (!this.searchTermOrigin || this.searchTermOrigin.trim() === "") {
        this.errorMessages.push(this.$t("error.departureStation"));
        isValid = false;
      }

      if (!this.departureDateTrain) {
        this.errorMessages.push(this.$t("error.departureDate"));
        isValid = false;
      }

      if (this.tripType === "roundTrip") {
        if (
          !this.searchTermDestination ||
          this.searchTermDestination.trim() === ""
        ) {
          this.errorMessages.push(this.$t("error.destinationStation"));
          isValid = false;
        }

        if (!this.returnDateTrain) {
          this.errorMessages.push(this.$t("error.returnDate"));
          isValid = false;
        } else if (this.returnDateTrain < this.departureDateTrain) {
          this.errorMessages.push(this.$t("error.dateBefore"));
          isValid = false;
        }
      }

      let customFieldsValid = true;
      let invalidCustomFields = [];

      this.companyInfo.customFields.forEach((field) => {
        if (field.mandatory) {
          if (
            !this.customFieldFormData[field.name] ||
            this.customFieldFormData[field.name] === ""
          ) {
            customFieldsValid = false;
            isValid = false;
            invalidCustomFields.push(this.getTitle(field));
          }
        }
      });

      if (!customFieldsValid) {
        let message = this.$t("error.customFields");
        message += ` (${invalidCustomFields.join(", ")})`;
        this.errorMessages.push(message);
      }

      // Car rental validation
      if (this.carRentalSelected) {
        if (
          !this.carData.selectedCarClass ||
          this.carData.selectedCarClass === ""
        ) {
          this.errorMessages.push("Please select a car category");
          isValid = false;
        }
        if (
          !this.carData.selectedCarTransmissionType ||
          this.carData.selectedCarTransmissionType === ""
        ) {
          this.errorMessages.push("Please select transmission type");
          isValid = false;
        }
        if (!this.carData.driver || !this.carData.driver.firstName) {
          this.errorMessages.push("Please select a driver");
          isValid = false;
        }
      }

      if (!isValid) {
        let notificationMessage = `${this.$t("error.genericIntro")} <ul>
      <li>- ${this.errorMessages.join("</li><li>- ")}</li></ul>`;

        this.notification.title = this.$t("error.genericTitle");
        this.notification.message = notificationMessage;
        this.notification.level = "danger";
        this.notification.show = true;

        setTimeout(() => {
          this.notification.show = false;
        }, 10 * 1000);
        return false;
      } else {
        return true;
      }
    },
    getTitle(field) {
      return this.getFieldNameTranslation(field);
    },
    toggleNotification() {
      this.notification.show = !this.notification.show;
    },
    track(eventName, data) {
      // your tracking implementation
      // e.g. console.log("Tracking:", eventName, data);
    },
    getFieldNameTranslation(field) {
      // your custom method if needed
      return field.label || field.name;
    },
    selectOriginStation(station) {
      this.originStation = `${station.name} - ${station.city}, ${station.country}`;
      this.searchTermOrigin = `${station.name} - ${station.city}, ${station.country}`;
      this.originTrainStationSelected = false;
    },
    selectDestinationStation(station) {
      this.destinationStation = `${station.name} - ${station.city}, ${station.country}`;
      this.searchTermDestination = `${station.name} - ${station.city}, ${station.country}`;
      this.destinationTrainStationSelected = false;
    },
    async searchStation(newVal) {
      if (!newVal || newVal.trim() === "") {
        this.destinationStations = [];
        return;
      }

      const url = `${this.AVAILABILITY_API_URL}/map/stations`;
      const response = await this.axios.get(url, {
        params: {
          query: newVal,
          language: this.$i18n.locale
        }
      });

      const stations = (response.data.suggestions || []).map((s) => ({
        name: s.name,
        city: s.context?.place?.name || "",
        country: s.context?.country?.name || ""
      }));

      // Deduplicate using Set with composite key
      const seen = new Set();
      const uniqueStations = stations.filter((station) => {
        const key = `${station.name}|${station.city}|${station.country}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });

      return uniqueStations;
    }
  }
};
</script>
<style scoped>
.details__title {
  background: url("@/assets/images/train-bg.webp") center center no-repeat;
  background-size: cover;
}

.dropdown-item {
  cursor: pointer;
  padding: 5px;
}

.dropdown-item:hover {
  background-color: #f1f1f1;
}
</style>
